import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [systemStats, setSystemStats] = useState(null);

  useEffect(() => {
    const fetchSystemStats = async () => {
      try {
        const response = await api.get('/api/system/stats');
        console.log('🔄 Dashboard Stats Updated:', new Date().toLocaleTimeString(), {
          cpu: response.data.cpu?.usagePercent,
          memory: response.data.memory?.usagePercent,
          disk: response.data.disk?.usagePercent
        });
        setSystemStats(response.data);
      } catch (error) {
        console.error('Failed to fetch system stats:', error);
      }
    };

    fetchSystemStats();
    const interval = setInterval(fetchSystemStats, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Helper function to format uptime
  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `up ${days} ${days === 1 ? 'week' : 'weeks'}, ${hours} ${hours === 1 ? 'hour' : 'hours'}, ${minutes} minutes`;
  };

  // Helper function to create circular progress indicator
  const CircularProgress = ({ percentage, color }) => {
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex', width: 100, height: 100 }}>
        <svg width="100" height="100">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="8"
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.2rem'
          }}
        >
          {percentage}%
        </Box>
      </Box>
    );
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3, fontSize: '1.4rem' }}>
        Καλώς ήρθες, {user?.firstname || user?.username}!
      </Typography>
      
      <Grid container spacing={3}>
        {/* Full Width System Stats Grid */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* RAM Card */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: 'rgb(0, 57, 108)', color: 'white', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Ram
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4A9EFF', mr: 1 }} />
                        In Use: {systemStats?.memory?.used || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#666', mr: 1 }} />
                        Available: {systemStats?.memory?.available || 'N/A'}
                      </Typography>
                    </Box>
                    {systemStats?.memory?.usagePercent && (
                      <CircularProgress percentage={systemStats.memory.usagePercent} color="#4A9EFF" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* CPU Usage Card */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: 'rgb(0, 57, 108)', color: 'white', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        CPU Usage
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#66BB6A', mr: 1 }} />
                        Utilization: {systemStats?.cpu?.usage || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#666', mr: 1 }} />
                        Cores: {systemStats?.cpu?.cores || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#666', mr: 1 }} />
                        Speed/Core: {systemStats?.cpu?.speed || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#666', mr: 1 }} />
                        Threads: {systemStats?.cpu?.threads || 'N/A'}
                      </Typography>
                    </Box>
                    {systemStats?.cpu?.usagePercent !== undefined && (
                      <CircularProgress percentage={systemStats.cpu.usagePercent} color="#66BB6A" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Hard Disk Usage Card */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: 'rgb(0, 57, 108)', color: 'white', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Hard Disk Usage
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#42A5F5', mr: 1 }} />
                        Total: {systemStats?.disk?.total || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#666', mr: 1 }} />
                        Used: {systemStats?.disk?.used || 'N/A'}
                      </Typography>
                    </Box>
                    {systemStats?.disk?.usagePercent && (
                      <CircularProgress percentage={systemStats.disk.usagePercent} color="#42A5F5" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Uptime Overview Card */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: 'rgb(0, 57, 108)', color: 'white', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Uptime Overview
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#666', mr: 1 }} />
                    {formatUptime(systemStats?.uptime?.seconds)}
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#666', mr: 1 }} />
                    PID: {systemStats?.process?.pid || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box sx={{ mt: 4 }}>
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'left', 
            color: '#999',
            fontSize: '0.75rem',
            mb: 1.5
          }}
        >
          Zoom Detective by Merimna Institute v.20.0 Beta
        </Typography>
        
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'left', 
            color: '#999',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            mb: 0.5
          }}
        >
          Changelogs
        </Typography>
        
        <Box component="ul" sx={{ 
          m: 0, 
          pl: 2.5, 
          color: '#999',
          fontSize: '0.7rem',
          lineHeight: 1.6,
          mb: 2
        }}>
          <li>Stable up to 30 meetings and up to 1,200 participants</li>
          <li>Performance optimization</li>
          <li>SMS functionality</li>
          <li>VIP Detector functionality</li>
          <li>Bot and API calls optimizations with rate limits</li>
          <li>Students, Groups, Lessons synchronization via API</li>
          <li>Safety and Security</li>
        </Box>
        
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'left', 
            color: '#999',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            mb: 0.5
          }}
        >
          Next Plans
        </Typography>
        
        <Box component="ul" sx={{ 
          m: 0, 
          pl: 2.5, 
          color: '#999',
          fontSize: '0.7rem',
          lineHeight: 1.6
        }}>
          <li>Calendar</li>
          <li>Students Notes with WebSocket functionality</li>
          <li>Monitoring Users Live Chat</li>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
